// src/components/ProfileHeader/ProfileHeader.js
import React, { useState } from 'react';
import { User, Settings, UserPlus, UserCheck, Clock, Ban } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SettingsMenu from './SettingsMenu';
import ConfirmationModal from '../ConfirmationModal';
import './ProfileHeader.css';

const ProfileHeader = ({ 
  user, 
  isOwnProfile,
  friendshipStatus = 'none',
  onEditProfile,
  onLogout,
  onSendRequest,
  onCancelRequest,   // NEW
  onAcceptRequest,   // NEW
  onDeclineRequest,  // NEW
  onUnfriend,        // optional, for friends
  className 
}) => {
  const { t } = useTranslation();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleUnfriendClick = () => {
    setConfirmState({
      type: 'unfriend',
      title: t('friends.unfriend_title', { name: user?.displayName || '' }),
      message: t('friends.unfriend_message', { name: user?.displayName || '' }),
      confirmText: t('friends.unfriend'),
      onConfirm: onUnfriend
    });
  };

  const handleCancelRequestClick = () => {
    setConfirmState({
      type: 'cancel',
      title: t('friends.cancel_request_title', { name: user?.displayName || '' }),
      message: t('friends.cancel_request_message', { name: user?.displayName || '' }),
      confirmText: t('friends.cancel_request'),
      onConfirm: onCancelRequest
    });
  };

  const handleDeclineRequestClick = () => {
    setConfirmState({
      type: 'decline',
      title: t('friends.decline_request_title', { name: user?.displayName || '' }),
      message: t('friends.decline_request_message', { name: user?.displayName || '' }),
      confirmText: t('friends.decline'),
      onConfirm: onDeclineRequest
    });
  };

  const handleConfirmAction = async () => {
    if (!confirmState?.onConfirm) return;
    
    setError(null);
    setIsLoading(true);
    
    try {
      const result = await confirmState.onConfirm();
      if (result === false) {
        // The action returned false, indicating an error
        setError(t('common.action_failed'));
      } else {
        // Success - close the modal
        setConfirmState(null);
      }
    } catch (e) {
      console.error('Confirmation action failed:', e);
      setError(e?.message || t('common.action_failed'));
      // Keep modal open so user can retry or cancel
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    if (!isLoading) {
      setConfirmState(null);
      setError(null);
    }
  };

  const renderActionButton = () => {
    if (isOwnProfile) {
      return (
        <button 
          onClick={onEditProfile}
          className="profile-action-button edit-button"
        >
          {t('profile.edit_profile')}
        </button>
      );
    }

    switch (friendshipStatus) {
      case 'friends':
        return (
          <button 
            className="profile-action-button connected-button"
            onClick={handleUnfriendClick}
          >
            <UserCheck className="profile-action-button-icon" />
            {t('friends.connected')}
          </button>
        );
      case 'requested':
        return (
          <button 
            className="profile-action-button requested-button"
            onClick={handleCancelRequestClick}
          >
            <Clock className="profile-action-button-icon" />
            {t('friends.requested')}
          </button>
        );
      case 'pending':
        return (
          <div className="profile-action-split">
            <button 
              className="profile-action-button accept-button" 
              onClick={onAcceptRequest}
            >
              {t('friends.accept')}
            </button>
            <button 
              className="profile-action-button decline-button" 
              onClick={handleDeclineRequestClick}
            >
              {t('friends.decline')}
            </button>
          </div>
        );
      case 'blocked':
        return (
          <button className="profile-action-button blocked-button" disabled>
            <Ban className="profile-action-button-icon" />
            {t('friends.blocked')}
          </button>
        );
      case 'blocked_by':
        return (
          <button className="profile-action-button blocked-button" disabled>
            <Ban className="profile-action-button-icon" />
            {t('friends.blocked_by')}
          </button>
        );
      default:
        return (
          <button 
            onClick={onSendRequest}
            className="profile-action-button connect-button"
          >
            <UserPlus className="profile-action-button-icon" />
            {t('friends.connect')}
          </button>
        );
    }
  };

  return (
    <div className={`profile-header ${className || ''}`}>
      {isOwnProfile && (
        <>
        <button 
          onClick={() => setIsSettingsOpen(true)} 
          className="profile-settings-button"
          aria-label={t('settings.title')}
        >
          <Settings className="profile-settings-icon" />
        </button>
        <SettingsMenu 
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          onLogout={onLogout}
        />
      </>
    )}
      
      <div className="profile-header-content">
        <div className="profile-header-main">
          <div className="profile-info-container">
            <div className="profile-picture-container">
              <div className="profile-picture">
                {user.profilePictureUrl ? (
                  <img 
                    src={user.profilePictureUrl} 
                    alt={t('profile.profile_picture_alt', { name: user.displayName })}
                  />
                ) : (
                  <User className="profile-picture-placeholder" />
                )}
              </div>
            </div>
            
            <div className="profile-info">
              <div>
                <h1 className="profile-name">{user.displayName}</h1>
                <span className="profile-username">@{user.username}</span>
              </div>
              <p className="profile-bio">{user.bio}</p>
            </div>
          </div>
        </div>
        
        {renderActionButton()}
      </div>
      
      {confirmState && (
        <ConfirmationModal
          isOpen={true}
          onClose={handleCloseModal}
          onConfirm={handleConfirmAction}
          title={confirmState.title}
          message={
            <>
              {confirmState.message}
              {error && (
                <div style={{ color: 'var(--semantic-error-bg)', marginTop: '10px' }}>
                  {error}
                </div>
              )}
            </>
          }
          confirmText={confirmState.confirmText}
          cancelText={t('common.cancel')}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default ProfileHeader;